import { useEffect, useMemo } from 'react';
import {
  AutocompleteOption,
  FileUpload,
  FormCheckbox,
  LwFormDate,
  LwFormSelect,
  UploadedFileList,
} from 'redesign';
import { Box, Grid, styled, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { DateTime } from 'luxon';
import { Section } from 'shared/components/common/form-section';
import { IconInfoRedesigned } from 'assets/img';
import { accreditationTypeOptions } from 'hq/components/views/accreditations/form/utils';
import { FlexWorkerSelect } from 'hq/components/views/flexWorkers/flex-worker-select';
import { WorkerList } from 'hq/services/flex-worker-service.types';

export type AccreditationFormTypes = {
  files?: File[];
  accreditationType: string | null;
  certificate: string | null;
  startDate: string | null;
  isWithoutDocument: boolean;
  expirationDate: string | null;
  flexWorkerOption: AutocompleteOption | null;
  hasPreviouslyUploadedDocument: boolean;
  accreditationState?: string;
};

export type CertificateWithFileUrl = {
  id: string;
  name: string;
  canHaveReceipt: boolean;
  fileUrl?: string;
};

type AccreditationFormProps = {
  certificates: CertificateWithFileUrl[];
  formState: 'create' | 'update';
  flexWorkers?: WorkerList[];
  flexWorkerOption?: AutocompleteOption | null;
  flexWorkerSearch?: string;
  setFlexWorkerSearch?: (value: string) => void;
  setFlexWorkerOption?: (value: AutocompleteOption | null) => void;
  flexWorkerDisabled?: boolean;
};

export const AccreditationForm = (props: AccreditationFormProps) => {
  const formMethods = useFormContext<AccreditationFormTypes>();

  const {
    isWithoutDocument,
    accreditationType,
    hasPreviouslyUploadedDocument,
    accreditationState,
    certificate,
  } = formMethods.watch();

  const filteredCertificates = useMemo(() => {
    if (accreditationType !== 'receipt') {
      return props.certificates;
    }
    return props.certificates.filter((certificate) => certificate.canHaveReceipt === true);
  }, [accreditationType, props.certificates]);

  const clearFiles = () => {
    formMethods.setValue('files', []);
  };

  useEffect(() => {
    if (props.formState === 'update' && hasPreviouslyUploadedDocument) {
      const existingFile = { name: certificate };
      formMethods.setValue('files', [existingFile as File], { shouldDirty: true });
    }
  }, [props.formState, hasPreviouslyUploadedDocument, certificate]);

  return (
    <Box width={'100%'}>
      <Grid container>
        {props.formState === 'update' && accreditationState ? (
          <Section sectionLabel="Status">
            <Grid item xs={4}>
              <AccreditationState variant="body1">
                Huidige staat: {accreditationState}
              </AccreditationState>
            </Grid>
          </Section>
        ) : null}
        {props.flexWorkers && (
          <Section sectionLabel="Kies een flexwerker">
            <Grid item xs={4}>
              <FlexWorkerSelect
                workers={props.flexWorkers}
                flexWorkerOption={props.flexWorkerOption ?? null}
                flexWorkerSearch={props.flexWorkerSearch ?? ''}
                setFlexWorkerSearch={props.setFlexWorkerSearch ?? (() => {})}
                setFlexWorkerOption={props.setFlexWorkerOption ?? (() => {})}
                disabled={props.flexWorkerDisabled || props.formState === 'update'}
              />
            </Grid>
          </Section>
        )}
        <Section sectionLabel="Selecteer certificaat">
          <Grid item xs={4}>
            <Box display="flex" flex={1}>
              <LwFormSelect
                control={formMethods.control}
                name="accreditationType"
                label="Type"
                defaultLabel="Selecteer een accreditatietype"
                options={accreditationTypeOptions}
                onChange={(e) => {
                  formMethods.setValue('accreditationType', e.target.value);
                }}
                disabled={props.formState === 'update'}
              />
            </Box>
          </Grid>
          <Grid item xs={4}>
            <LwFormSelect
              control={formMethods.control}
              name="certificate"
              label="Certificaten"
              options={filteredCertificates.map((certificate) => ({
                value: certificate.id,
                label: certificate.name,
              }))}
              rules={{ required: true }}
            ></LwFormSelect>
          </Grid>

          <Grid item xs={4}>
            <FormCheckbox
              name="isWithoutDocument"
              label="Een certificaat aanmaken zonder certificaat document"
              control={formMethods.control}
              disabled={hasPreviouslyUploadedDocument}
              onChange={(e) => {
                e === true ? clearFiles() : null;
                formMethods.setValue('startDate', null);
              }}
            />
            <Box display="flex" alignItems="center" gap={4} mt={2}>
              <IconInfoRedesigned width={24} height={24} /> Je moet een startdatum instellen en het
              certificaat document uploaden na de startdatum
            </Box>
          </Grid>
          <Grid container item xs={4} spacing={4}>
            <Grid item xs={6}>
              <LwFormDate
                name="startDate"
                label="Startdatum"
                control={formMethods.control}
                minDate={DateTime.now().toISODate()}
                rules={{ required: isWithoutDocument ? 'Voer een startdatum in' : false }}
                disabled={!isWithoutDocument}
              />
            </Grid>
            <Grid item xs={6}>
              <LwFormDate
                name="expirationDate"
                label="Vervaldatum"
                control={formMethods.control}
                minDate={DateTime.now().toISODate()}
              />
            </Grid>
          </Grid>
          <Grid item xs={4}>
            <Controller
              control={formMethods.control}
              name="files"
              defaultValue={[]}
              render={({ field: { value, onChange } }) => (
                <>
                  <FileUpload
                    disableUpload={isWithoutDocument}
                    onFilesChange={onChange}
                    multiple={true}
                  />
                  <UploadedFileList files={value} onClearFiles={() => onChange([])} />
                </>
              )}
            />
          </Grid>
        </Section>
      </Grid>
    </Box>
  );
};

const AccreditationState = styled(Typography)(({ theme }) => ({
  margin: theme.spacing(3, 0),
}));
